/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { getDocument } from '@atlaskit/browser-apis';
import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import Image from '@atlaskit/image/image';
import { type ExternalImageIdentifier } from '@atlaskit/media-client/identifier';
import { type MediaClientConfig } from '@atlaskit/media-core/auth';
import MediaViewer from '@atlaskit/media-viewer/media-viewer-loader';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { token } from '@atlaskit/tokens';

import { copyImageToClipboard } from './copyImageToClipboard';

// MediaViewer requires a MediaClient instance, but for external images
// no real auth is needed — a stub is sufficient.
const mediaClientConfig: MediaClientConfig = {
	authProvider: () => Promise.reject(new Error('Not required for external images')),
};

const messages = defineMessages({
	copy: {
		id: 'smart-knowledge-cards.image-generated.copy',
		defaultMessage: 'Copy',
		description:
			'Label for the button that copies the generated image displayed in the smart knowledge card.',
	},
	viewImage: {
		id: 'smart-knowledge-cards.image-generated.view-image',
		defaultMessage: 'View image: {imageName}',
		description:
			'Accessible label for the button that opens the generated image in the full-screen viewer.',
	},
});

const styles = cssMap({
	// The image is wrapped in a Pressable so it can be clicked to open the full-screen
	// MediaViewer. Reset the native button chrome so only the image is visible.
	imageButton: {
		backgroundColor: 'transparent',
		borderStyle: 'none',
		cursor: 'pointer',
		display: 'block',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		width: '100%',
		// Match the image's top corners so the focus ring hugs the visible shape.
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
		'&:focus-visible': {
			outlineColor: token('color.border.focused'),
			outlineOffset: token('space.negative.025'),
			outlineStyle: 'solid',
			outlineWidth: token('border.width.focused'),
		},
	},
	image: {
		display: 'block',
		// Cover the full width available in the resizable chat side panel and keep the
		// image's aspect ratio by letting the height scale automatically.
		width: '100%',
		height: 'auto',
		objectFit: 'cover',
		// Round only the top corners — the bottom corners are squared because the copy
		// button row sits directly underneath the image.
		borderTopLeftRadius: token('radius.medium'),
		borderTopRightRadius: token('radius.medium'),
	},
	actionRow: {
		backgroundColor: token('elevation.surface.sunken'),
		paddingInline: token('space.050'),
		borderBottomLeftRadius: token('radius.medium'),
		borderBottomRightRadius: token('radius.medium'),
	},
});

export interface ImageGeneratedProps {
	/**
	 * Accessible alternative text describing the generated image.
	 */
	altText: string;
	/**
	 * URL used to load and display the generated image.
	 */
	imageUrl: string;
}

/**
 * __ImageGenerated__
 *
 * Renders a backend-generated image with a copy action row underneath it.
 */
export const ImageGenerated = function ImageGenerated({
	altText,
	imageUrl,
}: ImageGeneratedProps): JSX.Element {
	const { formatMessage } = useIntl();
	const [isViewerOpen, setIsViewerOpen] = useState(false);
	// Element that had focus before the viewer opened, so focus can be restored on close.
	const triggerRef = useRef<Element | null>(null);

	const identifier: ExternalImageIdentifier = useMemo(
		() => ({
			mediaItemType: 'external-image',
			dataURI: imageUrl,
			name: altText,
		}),
		[imageUrl, altText],
	);

	const openViewer = useCallback(() => {
		triggerRef.current = getDocument()?.activeElement ?? null;
		setIsViewerOpen(true);
	}, []);

	const closeViewer = useCallback(() => {
		const trigger = triggerRef.current;
		setIsViewerOpen(false);
		// Restore focus after React re-renders and the MediaViewer unmounts.
		requestAnimationFrame(() => {
			if (trigger instanceof HTMLElement) {
				trigger.focus();
			}
		});
	}, []);

	const onCopyClick = useCallback(async () => {
		try {
			await copyImageToClipboard(imageUrl);
		} catch {
			// TODO: Surface copy failures to the user. For now we intentionally do nothing
			// beyond logging so a failed copy fails silently.
		}
	}, [imageUrl]);

	return (
		<Stack>
			<Pressable
				xcss={styles.imageButton}
				onClick={openViewer}
				aria-label={formatMessage(messages.viewImage, { imageName: altText })}
			>
				{/* eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides */}
				<Image src={imageUrl} alt={altText} css={styles.image} />
			</Pressable>
			<Inline alignInline="end" xcss={styles.actionRow}>
				<Button appearance="subtle" iconBefore={CopyIcon} onClick={onCopyClick}>
					{formatMessage(messages.copy)}
				</Button>
			</Inline>
			{isViewerOpen && (
				<MediaViewer
					mediaClientConfig={mediaClientConfig}
					selectedItem={identifier}
					items={[identifier]}
					collectionName=""
					onClose={closeViewer}
				/>
			)}
		</Stack>
	);
};
